import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, NavLink, Outlet, Link } from 'react-router-dom';
import { adminApi } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LayoutDashboard, Users, ShieldCheck, ArrowDownToLine, ArrowUpFromLine, Receipt, Percent, Building2, ScrollText, Settings, LogOut, Globe, ChevronDown, ShieldAlert } from 'lucide-react';
import { NotificationsBell } from '@/components/fintech/NotificationsBell';

const NAV = [
  { to: '/admin', end: true, icon: LayoutDashboard, key: 'admin.overview' },
  { to: '/admin/users', icon: Users, key: 'admin.users' },
  { to: '/admin/kyc', icon: ShieldCheck, key: 'admin.kyc_queue' },
  { to: '/admin/aml', icon: ShieldAlert, key: 'admin.aml' },
  { to: '/admin/deposits', icon: ArrowDownToLine, key: 'admin.deposits' },
  { to: '/admin/withdrawals', icon: ArrowUpFromLine, key: 'admin.withdrawals' },
  { to: '/admin/transactions', icon: Receipt, key: 'admin.transactions' },
  { to: '/admin/rates', icon: Percent, key: 'admin.rates' },
  { to: '/admin/companies', icon: Building2, key: 'admin.companies' },
  { to: '/admin/audit', icon: ScrollText, key: 'admin.audit_log' },
  { to: '/admin/admins', icon: Settings, key: 'admin.admins' },
];

export function AdminLayout() {
  const { t, lang, langs, setLang } = useLanguage();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('concorde_admin_token');
    if (!token) { setLoading(false); return; }
    adminApi.get('/admin/auth/me').then(r => setAdmin(r.data.admin)).catch(() => localStorage.removeItem('concorde_admin_token')).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[hsl(var(--muted-foreground))]">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const logout = () => { localStorage.removeItem('concorde_admin_token'); nav('/admin/login'); };

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex">
      <aside className="w-64 bg-[hsl(var(--brand-navy))] text-white sticky top-0 h-screen overflow-y-auto" data-testid="admin-sidebar">
        <div className="p-5 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2">
            <img src="/logo-light.png" alt="Concorde Financial Limited" className="h-9 w-auto" data-testid="admin-logo" />
            <span className="text-[10px] uppercase tracking-widest text-white/60">{t('admin.title')}</span>
          </Link>
        </div>
        <nav className="p-3 space-y-1">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.to.split('/').pop() || 'overview'}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? 'bg-white/10 text-white relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:rounded-full before:bg-[hsl(var(--brand-gold))]' : 'text-white/80 hover:text-white hover:bg-white/10'}`}
            >
              <item.icon className="h-4 w-4" />
              {t(item.key)}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex-1">
        <header className="sticky top-0 z-30 border-b bg-white">
          <div className="px-6 h-16 flex items-center justify-between">
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Signed in as <span className="font-medium text-[hsl(var(--foreground))]">{admin.email}</span> · {admin.role}</div>
            <div className="flex items-center gap-2">
              <NotificationsBell apiClient={adminApi} dataTestPrefix="admin" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="gap-1"><Globe className="h-4 w-4" />{langs.find(l => l.code === lang)?.label}<ChevronDown className="h-3 w-3" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">{langs.map(l => <DropdownMenuItem key={l.code} data-testid={`admin-lang-${l.code}`} onSelect={() => setLang(l.code)}>{l.label} — {l.full}</DropdownMenuItem>)}</DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout"><LogOut className="h-4 w-4 mr-1" />{t('header.logout')}</Button>
            </div>
          </div>
        </header>
        <main className="p-6"><Outlet context={{ admin }} /></main>
      </div>
    </div>
  );
}
